/**
 * 解决方案子方案截断、首页方案卡片收集与关联案例过滤的纯函数集合。
 *
 * 对应设计文档 Correctness Properties:
 * - Property 3: 分类子方案展开截断规则（Requirements 6.9, 6.10）
 * - Property 6: 首页方案卡片收集顺序与数量上限（Requirements 9.1）
 * - Property 8: 关联案例分类过滤与去重截断（Requirements 11.4, 11.5, 11.6）
 */

/** 参与截断/排序的子方案最小字段集合（Nav_Dropdown、首页区块等场景复用） */
export interface SortableSubSolution {
  id: string | number;
  sort: number;
}

/** 参与首页收集的分类节点最小字段集合（含按 sort 升序排列的子方案数组） */
export interface SolutionCategoryWithSubs<T extends SortableSubSolution = SortableSubSolution> {
  id: string;
  sort: number;
  subSolutions: readonly T[];
}

/** 参与关联案例过滤的案例最小字段集合 */
export interface FilterableCase {
  id: string | number;
  /** 案例所属前台解决方案分类标识（对应 biz_case.solution） */
  solution: string;
  status: string;
  sort: number;
}

/** Nav_Dropdown 默认展示的子方案数量上限 */
const SUB_SOLUTIONS_VISIBLE_LIMIT = 6;
/** Home_Solutions_Section 收集展示的方案数量上限 */
const HOME_SOLUTIONS_LIMIT = 3;
/** 关联案例区域展示的案例数量上限 */
const RELATED_CASES_LIMIT = 6;

function bySortAscending<T extends { sort: number }>(a: T, b: T): number {
  return a.sort - b.sort;
}

/**
 * 按 `sort` 升序排列分类下的子方案；未展开（`expanded=false`）时截取前 6 条，
 * 展开（`expanded=true`）时返回全部（仍按 `sort` 升序）。
 *
 * Validates: Requirements 6.9, 6.10
 */
export function takeVisibleSubSolutions<T extends SortableSubSolution>(
  subSolutions: readonly T[],
  expanded: boolean,
): T[] {
  const sorted = [...subSolutions].sort(bySortAscending);
  return expanded ? sorted : sorted.slice(0, SUB_SOLUTIONS_VISIBLE_LIMIT);
}

/**
 * 「展开全部」入口是否应展示：仅当子方案数量大于 6 时为真。
 *
 * Validates: Requirements 6.9, 6.10
 */
export function shouldShowExpandToggle(list: readonly unknown[]): boolean {
  return list.length > SUB_SOLUTIONS_VISIBLE_LIMIT;
}

/**
 * 按分类 `sort` 升序遍历，同分类内保持子方案原有顺序依次展开，取前 3 个方案，
 * 用于 Home_Solutions_Section 的方案卡片收集。
 *
 * Validates: Requirements 9.1
 */
export function collectHomeSolutions<T extends SortableSubSolution>(
  categories: readonly SolutionCategoryWithSubs<T>[],
): T[] {
  const sortedCategories = [...categories].sort(bySortAscending);
  const collected: T[] = [];
  for (const category of sortedCategories) {
    for (const sub of category.subSolutions || []) {
      collected.push(sub);
      if (collected.length >= HOME_SOLUTIONS_LIMIT) return collected;
    }
  }
  return collected;
}

/**
 * 按分类配置的案例分类标识集合（`caseSolutionIds`）与 `status='published'` 过滤案例，
 * 按 `sort` 升序排列，按 `id` 去重，最多返回 6 条；`caseSolutionIds` 为空数组时返回空数组。
 *
 * Validates: Requirements 11.4, 11.5, 11.6
 */
export function selectRelatedCases<T extends FilterableCase>(
  cases: readonly T[],
  caseSolutionIds: readonly string[],
): T[] {
  if (caseSolutionIds.length === 0) return [];

  const allowedSolutionIds = new Set(caseSolutionIds);
  const filtered = cases.filter(
    (c) => allowedSolutionIds.has(c.solution) && c.status === "published",
  );
  const sorted = [...filtered].sort(bySortAscending);

  const seenIds = new Set<string | number>();
  const result: T[] = [];
  for (const item of sorted) {
    if (seenIds.has(item.id)) continue;
    seenIds.add(item.id);
    result.push(item);
    if (result.length >= RELATED_CASES_LIMIT) break;
  }
  return result;
}
